import type {
  RoomSettings,
  RoomStyle,
  RoomStylePreset
} from "../types";

type RoomStyleOption = {
  value: RoomStylePreset;
  name: string;
  description: string;
  wallColor: string;
  floor: RoomSettings["floor"];
  lighting: RoomSettings["lighting"];
};

export const ROOM_STYLE_OPTIONS: RoomStyleOption[] = [
  {
    value: "light",
    name: "Светлый",
    description: "Воздушный фон и светлый паркет",
    wallColor: "#f7f5f0",
    floor: "light",
    lighting: "day"
  },
  {
    value: "warm",
    name: "Тёплый",
    description: "Бежевые стены и натуральное дерево",
    wallColor: "#e8dfd1",
    floor: "laminate",
    lighting: "day"
  },
  {
    value: "modern",
    name: "Современный",
    description: "Сдержанный серо-бежевый интерьер",
    wallColor: "#d8d2c8",
    floor: "tile",
    lighting: "day"
  },
  {
    value: "neutral",
    name: "Нейтральный",
    description: "Спокойный фон без цветового акцента",
    wallColor: "#e8e4dc",
    floor: "laminate",
    lighting: "day"
  }
];

const styleValues = new Set<RoomStyle>([
  ...ROOM_STYLE_OPTIONS.map(({ value }) => value),
  "custom"
]);

export const isRoomStyle = (value: unknown): value is RoomStyle =>
  typeof value === "string" && styleValues.has(value as RoomStyle);

export const getRoomStyleOption = (style: RoomStylePreset) =>
  ROOM_STYLE_OPTIONS.find(({ value }) => value === style) ??
  ROOM_STYLE_OPTIONS[0]!;

export const roomStyleName = (style: RoomStyle) =>
  style === "custom" ? "Ручная настройка" : getRoomStyleOption(style).name;

export const roomStylePatch = (
  style: RoomStylePreset
): Pick<RoomSettings, "wallColor" | "floor" | "lighting" | "style"> => {
  const option = getRoomStyleOption(style);
  return {
    wallColor: option.wallColor,
    floor: option.floor,
    lighting: option.lighting,
    style
  };
};

export const inferRoomStyle = (
  room: Pick<RoomSettings, "wallColor" | "floor" | "lighting">
): RoomStyle => {
  const color = room.wallColor.toLowerCase();
  return (
    ROOM_STYLE_OPTIONS.find(
      (style) =>
        style.wallColor.toLowerCase() === color &&
        style.floor === room.floor &&
        style.lighting === room.lighting
    )?.value ?? "custom"
  );
};
